/**
 * The second-factor half of signing in to the platform console.
 *
 * WHY THIS FILE EXISTS, AND WHY IT IS A COPY.
 *
 * The admin console had NO multi-factor handling at all. Its sign-in form
 * called signInWithEmailAndPassword inside a bare `catch {}` and reported every
 * possible failure as "That email and password combination was not accepted."
 * So a platform admin who had enrolled an authenticator app - which this
 * product actively encourages, and whose absence the console itself flags as a
 * risk - could not sign in, and was told their password was wrong. The password
 * was right. Firebase was asking for the code, and nobody was listening.
 *
 * `web/src/lib/mfa.ts` has done this correctly on the tenant side since MFA
 * shipped. This is a deliberate copy of only the SIGN-IN half rather than a
 * shared module, because web/ and admin/ are separate Vite apps with no shared
 * source root; introducing one for four functions is a build change to fix a
 * typing problem. The enrolment half is NOT copied - the console does not enrol
 * factors, and copying code nobody calls is how a duplicate silently drifts.
 *
 * If this behaviour ever changes, change it in both. The pairing that matters:
 * isMfaRequiredError -> mfaResolver -> completeMfaSignIn.
 */

import {
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from './firebase';

export const isMfaRequiredError = (e: unknown): e is MultiFactorError =>
  !!e && typeof e === 'object' && (e as { code?: string }).code === 'auth/multi-factor-auth-required';

export function mfaResolver(error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

/** Only TOTP is offered. A phone factor is skipped rather than falling back to SMS. */
export async function completeMfaSignIn(resolver: MultiFactorResolver, code: string): Promise<void> {
  const totp = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!totp) {
    throw new Error('This account has no authenticator app set up. Another platform admin can reset its two-factor login.');
  }
  await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(totp.uid, code.trim()));
}

/**
 * What actually went wrong, for a console used by four people.
 *
 * THE ONE THING STILL MERGED is "no such account" with "wrong password". Modern
 * Firebase returns auth/invalid-credential for both precisely so a login form
 * cannot be used to discover which addresses exist, and re-separating them here
 * would hand that back. Everything else is NAMED, because every other code has
 * a different fix and "not accepted" sent somebody hunting for a password
 * problem that did not exist.
 */
export function signInErrorText(e: unknown): string {
  const code = (e && typeof e === 'object' && (e as { code?: string }).code) || '';

  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'That email and password combination was not accepted.';
  }
  if (code === 'auth/invalid-verification-code') {
    /*
     * THE CLOCK IS NAMED FIRST, and deliberately.
     *
     * This project runs adjacentIntervals: 2 - a plus or minus one minute
     * window, tighter than Google's default of two and a half. That is a
     * deliberate security choice and it makes clock drift bite sooner than
     * people expect: a phone a couple of minutes out rejects every code it
     * shows, forever, and the obvious reading is "the app is broken" or "my
     * account is wrong". Neither sends anybody to the setting that fixes it.
     */
    return 'That code was not accepted. Enter the six digits with no space, and check the clock on '
      + 'the device running your authenticator - codes are time-based and this project allows only '
      + 'about a minute of drift. If it still fails, the code may have expired while you typed it; '
      + 'wait for the next one.';
  }
  if (code === 'auth/totp-challenge-timeout') {
    return 'That took too long and the sign-in expired. Enter your email and password again.';
  }
  if (code === 'auth/multi-factor-auth-required') {
    // Should never surface - it is the signal to SHOW the code box, not a
    // failure. Named so that if it ever does, it is not a mystery.
    return 'A second factor is required. Enter the code from your authenticator app.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Wait a few minutes and try again.';
  }
  if (code === 'auth/user-disabled') {
    return 'That account is disabled. Another platform admin can re-enable it.';
  }
  if (code === 'auth/unauthorized-domain') {
    // The one that appears the moment the console moves to a custom domain and
    // nobody has added it to Identity Platform yet. Named in full because the
    // fix is somewhere else entirely.
    return `This address (${window.location.hostname}) is not on the project's authorised domains list, so Firebase refused the sign-in. Add it under Authentication - Settings - Authorised domains.`;
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Email and password sign-in is switched off for this project.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Could not reach Firebase. Check the network and any content blocker.';
  }
  if (code) return `Sign-in failed: ${code}`;
  return e instanceof Error ? e.message : 'Sign-in failed.';
}
